import type {
  BooleanResponse,
  CreateGroupRequest,
  Group,
  GroupPagedList,
  GroupPagedListResponse,
  GroupResponse,
  UpdateGroupRequest,
  UserGroupListParams,
  UserGroupPagedList,
  UserGroupPagedListResponse,
  UserGroupUsersListParams,
} from '@apiContract';

import { receivedDigitalApi } from './index';

const groupsApi = receivedDigitalApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroups: builder.query<GroupPagedList, UserGroupListParams>({
      query: (params) => ({
        url: 'UserGroup',
        params,
      }),
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupPagedListResponse) => response.objectData,
    }),
    getGroupsForPick: builder.query<{ [key: number]: Group }, undefined>({
      query: () => ({
        url: 'UserGroup',
        perams: {
          'Paging.ItemsPerPage': 100000,
          'Paging.PageNumber': 0,
        },
      }),
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupPagedListResponse) => {
        if (!response.objectData.items) return {};
        return response.objectData.items?.reduce<{ [key: number]: Group }>((acc, item) => {
          if (item) {
            acc[item.id] = item;
          }
          return acc;
        }, {});
      },
    }),
    getGroupById: builder.query<Group, number>({
      query: (id) => `UserGroup/${id}`,
      providesTags: ['UserGroups'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    getGroupUsers: builder.query<UserGroupPagedList, UserGroupUsersListParams>({
      query: ({ id, ...params }) => ({
        url: `UserGroup/${id}/users`,
        params,
      }),
      providesTags: ['GroupUsers'],
      transformResponse: (response: UserGroupPagedListResponse) => response.objectData,
    }),
    createGroup: builder.mutation<Group, CreateGroupRequest>({
      query: (requestData) => ({
        url: 'UserGroup',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    updateGroup: builder.mutation<Group, UpdateGroupRequest>({
      query: (requestData) => ({
        url: 'UserGroup',
        method: 'PUT',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'GroupUsers', 'Users', 'User', 'UsersInfinite', 'UsersMin', 'PieceAutoActionConflicts'],
      transformResponse: (response: GroupResponse) => response.objectData,
    }),
    deleteGroup: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `UserGroup`,
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
    deleteGroups: builder.mutation<boolean, number[]>({
      query: (requestData) => ({
        url: 'UserGroup/list',
        method: 'DELETE',
        body: requestData,
      }),
      invalidatesTags: ['UserGroups', 'Users', 'User', 'UsersInfinite', 'UsersMin'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useUpdateGroupMutation,
  useGetGroupUsersQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupByIdQuery,
  useGetGroupsForPickQuery,
  useDeleteGroupsMutation,
} = groupsApi;
